<script setup>

import moment from 'moment';
import { computed, onMounted, ref } from 'vue';
import PresenceInputTime from './PresenceInputTime.vue';
import PresenceService from "@/api/services/newPresencia.service";
import { useAuthStore } from "@/app/stores/authStore";
import { useUiStore } from "@/app/stores/uiStore";
import errorHandler from "@/utils/plugins/ErrorHandler";
import { formatDate } from "@/utils/dateFormat";
import { useRoute } from 'vue-router';
import { getDefaultTypeAfter, normalizeFollowingSignTypes, normalizeSignTypesByOrder } from '@/utils/presenceSignSequence';

// eslint-disable-next-line no-undef
const props = defineProps({
    presenceSigns: Object,
})
// eslint-disable-next-line no-undef
const emit = defineEmits(['closeDialog', 'changePresence'])
const authStore = useAuthStore();
const uiStore = useUiStore();
const route = useRoute();
const user = computed(() => authStore.getUserLogged);

// Computed
const currentSign = computed(() => {
    return props.presenceSigns;
});
const signDate = computed(() => {
    return moment(props.presenceSigns.date, 'DD/MM/YYYY').toDate();
});

const disabledAddNewSign = computed(() => {
    let empty = false;
    allSigns.value.forEach((val) => {
        if (!val.sign) empty = true;
    })
    return empty;
});

const allSigns = ref([]);
const addTime = ref();
const showModalWarning = ref(false);
const isSameTime = ref(false);
const validationError = ref(null);
const timeSequenceError = ref(null);

const flattenSignsForDialog = (signs) => {
    const flattened = [];

    for (let i = 0; i < signs.length; i++) {
        const signObj = signs[i];
        for (const recType of ['entry', 'resume', 'exit', 'pause']) {
            const record = signObj[recType];
            if (!record || !record.time) continue;

            const temp = {
                sign: getDateFromSignIn(record.time),
                type: recType,
                ...(record.pauseReason && { pauseReason: record.pauseReason }),
                ...(record.pauseReasonOtherText && { pauseReasonOtherText: record.pauseReasonOtherText }),
            };

            if (record.originalWorkRecord) {
                temp.id = record.id;
                temp.originalWorkRecord = record.originalWorkRecord;
            } else {
                temp.originalWorkRecord = record.id;
            }

            if (record.class) temp.class = record.class;
            flattened.push(temp);
        }
    }

    return flattened;
}

const buildDialogSigns = (presenceSigns) => {
    const baseSigns = flattenSignsForDialog(presenceSigns.signs || []);
    const requestedWorkRecords = presenceSigns.requestedWorkRecords || [];

    if (!requestedWorkRecords.length) {
        return baseSigns;
    }

    const requestedOriginalIds = new Set(
        requestedWorkRecords
            .filter(record => record.originalId)
            .map(record => record.originalId)
    );

    const mergedSigns = baseSigns.filter(sign => !requestedOriginalIds.has(sign.originalWorkRecord));

    requestedWorkRecords.forEach((record) => {
        mergedSigns.push({
            sign: getDateFromSignIn(record.time),
            id: record.id,
            existingRequestId: record.existingRequestId,
            originalWorkRecord: record.originalWorkRecord,
            type: record.type,
            class: record.class,
            ...(record.pauseReason && { pauseReason: record.pauseReason }),
            ...(record.pauseReasonOtherText && { pauseReasonOtherText: record.pauseReasonOtherText }),
        });
    });

    return mergedSigns;
}

onMounted(() => {
    allSigns.value = buildDialogSigns(props.presenceSigns);
    if (currentSign.value.newDay && allSigns.value.length === 0) {
        allSigns.value.push(
            { sign: null, class: 'new', modified: true, type: 'entry' },
            { sign: null, class: 'new', modified: true, type: 'exit' },
        );
    } else if (allSigns.value.length % 2 !== 0) {
        allSigns.value.push({
            sign: null,
            class: 'new',
            modified: true,
            type: getDefaultTypeAfter(allSigns.value[allSigns.value.length - 1]?.type),
        })
    }
    if (currentSign.value.newDay) {
        for (let i = 0; i < allSigns.value.length; i++) {
            allSigns.value[i].class = 'new';
            allSigns.value[i].modified = true;
        }
    }
    orderAllSigns();
    addTime.value = newDateWithActualTime();
})

const formatTitleText = (day) => {
    const formatted = moment(day, 'DD-MM-YYYY').format('DD [de] MMMM [de] YYYY');
    return formatted.replace(/(\sde\s)(\p{L})/u, (_, prefix, letter) => prefix + letter.toUpperCase());
}

const newDateWithActualTime = () => {
    const [day, month, year] = props.presenceSigns.date.split('/').map(Number);
    const fecha = new Date();
    fecha.setFullYear(year, month - 1, day);
    return fecha;
}

const orderAllSigns = () => {
    const temp = allSigns.value;
    for (let i = 0; i < temp.length; i++) {
        if (temp[i].sign != null) {
            if (!(temp[i].sign instanceof Date)) {
                const element = temp[i].sign;
                const [hours, minutes] = element.split(':').map(Number);
                const date = new Date(signDate.value);
                date.setHours(hours);
                date.setMinutes(minutes);
                date.setSeconds(0);
                date.setMilliseconds(0);
                temp[i].sign = date;
            }
        }
    }
    allSigns.value = [...temp.sort((a, b) => {
        if (!(a.sign instanceof Date) || !(b.sign instanceof Date)) return 0;
        return a.sign.getTime() - b.sign.getTime();
    })]
    return normalizeSignTypesByOrder(allSigns.value);
}


const getDateFromSignIn = (timeString) => {
    const newDate = new Date(signDate.value);
    const hours = parseInt(timeString.substring(0, 2), 10);
    const minutes = parseInt(timeString.substring(2, 4), 10);
    const seconds = parseInt(timeString.substring(4, 6), 10);
    return new Date(
        newDate.getFullYear(),
        newDate.getMonth(),
        newDate.getDate(),
        hours,
        minutes,
        seconds
    );
}

const changeNewSign = () => {
    if (allSigns.value[allSigns.value.length - 1].sign != null) {
        const lastType = allSigns.value[allSigns.value.length - 1].type;
        const firstNewType = getDefaultTypeAfter(lastType);
        const secondNewType = getDefaultTypeAfter(firstNewType);
        allSigns.value.push({ sign: null, class: 'new', modified: true, type: firstNewType });
        allSigns.value.push({ sign: null, class: 'new', modified: true, type: secondNewType });
    }
}

const markSignAsChanged = (sign) => {
    if (!sign) return;
    if (sign.class !== 'new') {
        sign.class = 'modification';
    }
    sign.modified = true;
}

const isSignDisabledUntilPreviousIsFilled = (index) => {
    if (!currentSign.value?.newDay || index === 0) return false;
    return !allSigns.value[index - 1]?.sign;
}

const getPreviousSignTime = (index) => (
    currentSign.value?.newDay && index > 0 ? allSigns.value[index - 1]?.sign : null
)

const getNextSignTime = (index) => (
    currentSign.value?.newDay ? allSigns.value[index + 1]?.sign : null
)

const showTimeSequenceError = (message) => {
    timeSequenceError.value = message;
}

// Cambios en inputs ya creados
const changeInputTime = (newInput) => {
    timeSequenceError.value = null;
    const [hours, minutes] = newInput.sign.split(':').map(Number);
    const date = new Date(signDate.value);
    date.setHours(hours);
    date.setMinutes(minutes);
    date.setSeconds(0);
    date.setMilliseconds(0);
    const el = allSigns.value[newInput.index];
    el.sign = date;
    markSignAsChanged(el);
    allSigns.value[newInput.index] = el;
    const normalizedChanges = orderAllSigns();
    normalizedChanges.forEach(({ index }) => {
        markSignAsChanged(allSigns.value[index]);
    });
}

// eslint-disable-next-line no-unused-vars
const changeType = ({ type, index }) => {
    const sign = allSigns.value[index];
    if (!sign) return;

    sign.type = type;
    markSignAsChanged(sign);

    const normalizedChanges = normalizeFollowingSignTypes(allSigns.value, index);
    normalizedChanges.forEach(({ index: changedIndex }) => {
        if (allSigns.value[changedIndex]?.sign !== null) {
            markSignAsChanged(allSigns.value[changedIndex]);
        }
    });
    allSigns.value = [...allSigns.value];
}

const validateAllSigns = () => {
    const typeLabels = {
        entry: 'Entrada', pause: 'Parada', resume: 'Reanudación', exit: 'Salida'
    };
    const stateLabels = {
        not_started: 'sin fichar', working: 'en jornada', paused: 'en pausa'
    };
    const validTransitions = {
        not_started: ['entry'],
        working: ['pause', 'exit'],
        paused: ['resume']
    };
    const stateFromType = (type) => {
        if (type === 'entry' || type === 'resume') return 'working';
        if (type === 'pause') return 'paused';
        return 'not_started'; // exit
    };

    const signs = allSigns.value.filter(s => s.sign !== null && s.type);

    let state = 'not_started';
    for (const sign of signs) {
        const allowed = validTransitions[state] || [];
        if (!allowed.includes(sign.type)) {
            const h = String(sign.sign.getHours()).padStart(2, '0');
            const m = String(sign.sign.getMinutes()).padStart(2, '0');
            return (
                `No se puede añadir una ${typeLabels[sign.type] || sign.type} ` +
                `a las ${h}:${m} cuando el estado es "${stateLabels[state]}".`
            );
        }
        state = stateFromType(sign.type);
    }
    return null;
};

// Botones Guardar y cancelar
const cancelDialog = () => {
    emit('closeDialog');
}

const savePresence = async () => {
    const empty = isEmptySignings();
    const changed = isChangedSignings();
    if (empty) {
        return;
    }
    if (changed) {
        emit('closeDialog');
        return;
    }
    const seqError = validateAllSigns();
    if (seqError) {
        validationError.value = seqError;
        showModalWarning.value = true;
        return;
    }
    const employeeIri = (route.params && route.params.id)
        ? "/api/employees/" + route.params.id
        : user.value['@id'];

    const batch = [];
    for (const element of allSigns.value) {
        if (element.sign === null || !element.modified || !element.class) continue;
        const item = { employee: employeeIri, type: element.type };
        item.requestedRecordedAt = formatDate(element.sign);
        if (element.type === 'pause') {
            item.pauseReason = element.pauseReason ? element.pauseReason : 'break';
            if (element.pauseReasonOtherText) item.pauseReasonOtherText = element.pauseReasonOtherText;
        }
        if (element.class === "new") {
            item.requestType = "new";
            if (element.existingRequestId) item.existingRequestId = element.existingRequestId;
        } else {
            item.requestType = "modification";
            if (element.originalWorkRecord) item.originalWorkRecord = element.originalWorkRecord;
            if (element.existingRequestId) item.existingRequestId = element.existingRequestId;
        }
        batch.push(item);
    }

    if (batch.length === 0) {
        emit('closeDialog');
        return;
    }

    uiStore.startLoading();
    try {
        await PresenceService.batchWorkRecordsRequestUser(batch);
        showModalWarning.value = true;
    } catch (err) {
        if (err.response?.status === 422) {
            const violations = err.response?.data?.violations || [];
            const backendMsg = violations[0]?.message;
            if (backendMsg) {
                validationError.value = backendMsg;
            } else {
                isSameTime.value = true;
            }
            showModalWarning.value = true;
        } else {
            errorHandler(err, null);
        }
    } finally {
        uiStore.stopLoading();
    }
}

//Comprobaciones para guardar
const isEmptySignings = () => {
    let empty = 0;
    allSigns.value.forEach((val) => {
        if (val.sign == null) empty++;
    })
    const last = allSigns.value[allSigns.value.length - 1];
    if (last.sign == null) {
        empty--;
    }
    return empty > 0;
}

const isChangedSignings = () => {
    let changed = 0;
    allSigns.value.forEach((val) => {
        if (val.modified) changed++;
    })
    const last = allSigns.value[allSigns.value.length - 1];
    if (last.sign == null) {
        changed--;
    }
    return changed === 0;
}

const okSavePresence = () => {
    showModalWarning.value = false;
    if (validationError.value) {
        validationError.value = null;
        return; // mantener el diálogo abierto para corregir
    }
    isSameTime.value = false;
    emit('closeDialog');
}


</script>

<template>
    <div class="main">
        <!-- Titulo Fecha Fichajes -->
        <div class="my-header">
            <div class="text-title d-flex justify-content-start align-content-start">
                <svg id="chronometer" version="1.1" xmlns="http://www.w3.org/2000/svg"
                    xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px" viewBox="0 0 233.766 233.766"
                    style="enable-background: new 0 0 233.766 233.766" xml:space="preserve">
                    <g>
                        <path
                            d="M188.169,79.423l6.059-6.058l2.906,2.906c1.465,1.465,3.384,2.197,5.304,2.197c1.919,0,3.839-0.732,5.303-2.196
                              c2.93-2.929,2.93-7.678,0.001-10.606l-16.419-16.421c-2.929-2.93-7.678-2.929-10.607-0.001c-2.93,2.929-2.93,7.678-0.001,10.607
                              l2.907,2.907l-6.06,6.059c-10.441-8.901-22.855-15.553-36.484-19.201c4.082-5.216,6.524-11.773,6.524-18.895
                              C147.602,13.781,133.821,0,116.883,0c-16.939,0-30.72,13.781-30.72,30.721c0,7.121,2.442,13.679,6.525,18.895
                              c-13.63,3.647-26.043,10.3-36.485,19.201l-6.059-6.059l2.907-2.907c2.929-2.929,2.928-7.678-0.001-10.607
                              c-2.93-2.928-7.678-2.929-10.607,0.001L26.024,65.666c-2.929,2.929-2.928,7.678,0,10.606c1.465,1.464,3.384,2.196,5.303,2.196
                              s3.839-0.732,5.304-2.197l2.906-2.906l6.058,6.058c-13.944,16.357-22.375,37.551-22.375,60.68
                              c0,51.646,42.017,93.663,93.662,93.663s93.662-42.017,93.662-93.663C210.545,116.974,202.113,95.78,188.169,79.423z
                              M101.163,30.721c0-8.668,7.052-15.721,15.72-15.721s15.72,7.053,15.72,15.721s-7.052,15.72-15.72,15.72
                              S101.163,39.389,101.163,30.721z M116.883,218.766c-43.375,0-78.662-35.288-78.662-78.663c0-43.374,35.288-78.662,78.662-78.662
                              c43.374,0,78.662,35.288,78.662,78.662C195.545,183.477,160.257,218.766,116.883,218.766z" />
                        <path d="M116.883,76.981c-4.142,0-7.5,3.357-7.5,7.5v55.621c0,4.143,3.358,7.5,7.5,7.5c4.143,0,7.5-3.357,7.5-7.5V84.481
                              C124.383,80.339,121.025,76.981,116.883,76.981z" />
                    </g>
                </svg>
                <b style="margin-top: 6px;">{{ formatTitleText(signDate) }}</b>
            </div>
        </div>
        <div class="my-body">
            <!-- Botón añadir fichajes -->
            <div class="title-presence d-flex justify-content-between align-content-center">
                <h2>Fichajes del día</h2>
                <div>
                    <font-awesome-layers class="fa-2x" style="margin-left: 5px" title="Añadir" @click="changeNewSign"
                        :class="disabledAddNewSign ? 'addNewDisable' : 'pointer'">
                        <font-awesome-icon icon="circle" class="addNewCircle" />
                        <font-awesome-icon icon="plus" class="fa-inverse" transform="shrink-6"
                            :style="{ color: !disabledAddNewSign ? '#474CE6' : '' }" />
                    </font-awesome-layers>
                </div>
            </div>
            <!-- Titulos fichajes -->
            <div class="row d-flex title-presence">
                <div class="col-6 d-flex justify-content-start align-content-start"
                    style="margin-left: 8px;margin-right: -8px;">
                    <span>Entrada</span>
                </div>
                <div class="col-6 d-flex justify-content-start align-content-start"
                    style="margin-left: 12px;margin-right: -12px;">
                    <span>Salida</span>
                </div>
            </div>
            <div class="row d-flex">
                <div v-for="(item, index) in allSigns" :key="item.sign + '-' + index"
                    class="mb-1 d-flex justify-content-start align-content-start col-md-6 el-col-xs-12">
                    <font-awesome-icon :icon="index % 2 === 0 ? 'sign-in-alt' : 'door-open'"
                        style="color: black; margin-top: 13px;" />
                    <PresenceInputTime :presenceHour="item.sign" :index="index" :myClass="item.class"
                        :presenceType="item.type" :presenceLastType="index > 0 ? allSigns[index - 1].type : null"
                        :disabled="isSignDisabledUntilPreviousIsFilled(index)"
                        :minimum-time="getPreviousSignTime(index)" :maximum-time="getNextSignTime(index)"
                        @change-input="changeInputTime" @change-type="changeType"
                        @invalid-input="showTimeSequenceError" />
                </div>
            </div>
            <p v-if="timeSequenceError" class="invalid-feedback d-block mb-2">
                {{ timeSequenceError }}
            </p>
            <div>
                <ul class="legend">
                    <li><span class="legend-circle green"></span> Nuevo</li>
                    <li><span class="legend-circle yellow"></span> Modificado</li>
                    <li><span class="legend-circle red"></span> Cancelado</li>
                </ul>
            </div>
            <!-- Botones Cancelar y Guardar -->
            <div class="row mt-4">
                <div class="col d-flex justify-content-end gap-3 flex-wrap buttons">
                        <button class="btn btn_yellow px-4" @click="cancelDialog()" style="width: 40%">
                        Cancelar
                    </button>

                    <button class="btn btn_red px-4" @click="savePresence()" style="width: 40%">
                        Guardar
                    </button>
                </div>
            </div>
        </div>
        <!-- Modal Aviso Guardado -->
        <el-dialog v-model="showModalWarning" @close="okSavePresence" width="375" :center="true" :lock-scroll="true"
            :show-close="false" class="el-dialog" style="margin-top: 20%;">
            <div class="warning-dialog-content">
                <h2 v-if="validationError">{{ validationError }}</h2>
                <h2 v-else-if="isSameTime">Ya existe una solicitud de creación pendiente para ese día y hora.</h2>
                <h2 v-else>Tu solicitud de cambio ha sido enviada.</h2>
                <button class="btn btn_yellow mt-1" @click="okSavePresence" style="width: 50%">
                    OK
                </button>
            </div>
        </el-dialog>
    </div>
</template>

<style lang="scss" scoped>
.text-title {
    margin-left: 5px;
    margin-top: 5px;
    width: 100%;
    height: auto;
    color: #474ce6;
    font-size: 2.5em;
    text-size-adjust: 50%;

    #chronometer {
        fill: $bg-blue;
        width: 1em;
        padding-bottom: 5px;
        margin-right: 7px;
    }
}

.title-presence {
    color: black;
    margin-left: 5px;

    span {
        font-weight: 800;
        font-size: 22px;
        margin-left: 7px;
    }
}

.inputSign {
    width: 85px;
    margin-left: 10px;
}

.modification:deep(.el-input__wrapper) {
    background-color: #d6c48a;
}

.warning-dialog-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: 10px;
    height: 100%;
}

.addNewCircle {
    color: #f7d20e;
}

.addNewDisable {
    .addNewCircle {
        color: rgb(150, 150, 150);
    }
}

.legend {
    list-style: none;
    display: flex;
    gap: 1rem;
    /* espacio entre elementos */
    padding: 0;
    margin: 0;
    margin-left: 10px;
    margin-top: 10px;
    align-items: center;
}

.legend li {
    display: flex;
    align-items: center;
    font-size: 0.9rem;
    color: #333;
}

.legend-circle {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    display: inline-block;
    margin-right: 6px;
}

.legend-circle.green {
    background-color: #99d68a;
}

.legend-circle.yellow {
    background-color: #e7e558;
}

.legend-circle.red {
    background-color: #ff0000;
}

@media (max-width: 400px){
    .buttons{
        button{
            width: 100% !important;
        }
    }
}

</style>
